import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

const tex = String.raw;

// ====== pomocnicze ======

function randomFrom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function unwrapTask(taskOrWrapper) {
  if (!taskOrWrapper) return taskOrWrapper;
  return taskOrWrapper.data ? taskOrWrapper.data : taskOrWrapper;
}

// prosty evaluator (jak w ReverseLateralSurfaceTask) – do sprawdzania odpowiedzi
function evaluateExpression(expr) {
  if (expr == null) return NaN;

  let cleaned = String(expr)
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/,/g, ".");

  if (!cleaned) return NaN;

  cleaned = cleaned
    .replace(/\\\\sqrt\{([^}]+)\}/g, "sqrt($1)")
    .replace(/\\sqrt\{([^}]+)\}/g, "sqrt($1)")
    .replace(/sqrt\{([^}]+)\}/g, "sqrt($1)")
    .replace(/sqrt\(([^)]+)\)/g, "sqrt($1)")
    .replace(/√/g, "sqrt");

  cleaned = cleaned
    .replace(/(\d)(sqrt)/g, "$1*sqrt")
    .replace(/(sqrt\([^)]*\))(\d)/g, "$1*$2");

  cleaned = cleaned.replace(/sqrt\(/g, "Math.sqrt(");

  if (!/^[0-9+\-*/().A-Za-z]*$/.test(cleaned)) {
    return NaN;
  }

  try {
    // eslint-disable-next-line no-new-func
    const f = new Function('"use strict"; return (' + cleaned + ");");
    const val = f();
    return typeof val === "number" && isFinite(val) ? val : NaN;
  } catch {
    return NaN;
  }
}

// ====== generatory wariantów ======

function genTriangleScalene() {
  // bok a i wysokość h_a do pola, b i c tylko do obwodu
  const a = randInt(4, 10);
  let hBase = randInt(3, 9);
  if ((a * hBase) % 2 === 1) hBase += 1; // żeby Pp było całkowite

  const b = randInt(3, 12);
  const c = randInt(3, 12);
  const h = randInt(3, 9);

  const baseArea = (a * hBase) / 2; // Pp
  const perimeter = a + b + c; // Op
  const lateralArea = perimeter * h; // Pb
  const totalArea = 2 * baseArea + lateralArea; // Pc

  return {
    variant: "triangle_scalene",
    isCube: false,
    a,
    b,
    c,
    baseHeight: hBase,
    h,
    aDisplay: String(a),
    bDisplay: String(b),
    cDisplay: String(c),
    baseHeightDisplay: String(hBase),
    hDisplay: String(h),
    perimeter,
    baseArea,
    lateralArea,
    totalAreaExpr: String(totalArea),
    totalAreaValue: totalArea,
  };
}

function genTriangleRight() {
  // z powszechnych trójek pitagorejskich
  const triples = [
    [3, 4, 5],
    [5, 12, 13],
    [6, 8, 10],
    [7, 24, 25],
  ];
  const [a0, b0, c0] = randomFrom(triples);
  const k = randInt(1, 3);

  const a = a0 * k;
  const b = b0 * k;
  const c = c0 * k;

  const h = randInt(3, 9);

  const baseArea = (a * b) / 2;
  const perimeter = a + b + c;
  const lateralArea = perimeter * h;
  const totalArea = 2 * baseArea + lateralArea;

  return {
    variant: "triangle_right",
    isCube: false,
    a,
    b,
    c,
    h,
    aDisplay: String(a),
    bDisplay: String(b),
    cDisplay: String(c),
    hDisplay: String(h),
    baseArea,
    perimeter,
    lateralArea,
    totalAreaExpr: String(totalArea),
    totalAreaValue: totalArea,
  };
}

function genTriangleEquilateral() {
  // bok parzysty, wysokość pryzmatu = k√3 => Pc = K√3
  const n = randInt(2, 6); // a = 2n
  const a = 2 * n;
  const hCoeff = randInt(1, 5); // h = hCoeff * √3
  const hValue = hCoeff * Math.sqrt(3);

  const baseCoeff = n * n; // Pp = n^2 √3
  const perimeter = 3 * a; // 6n
  const lateralCoeff = 6 * n * hCoeff; // Pb = 6n*hCoeff √3
  const totalCoeff = 2 * baseCoeff + lateralCoeff; // Pc = totalCoeff √3
  const totalValue = totalCoeff * Math.sqrt(3);

  return {
    variant: "triangle_equilateral",
    isCube: false,
    a,
    h: hValue,
    aDisplay: String(a),
    hDisplay: `${hCoeff}\\sqrt{3}`,
    perimeter,
    baseCoeff,
    lateralCoeff,
    totalCoeff,
    totalAreaExpr: `${totalCoeff}\\sqrt{3}`,
    totalAreaValue: totalValue,
    equiN: n,
    hRootCoeff: hCoeff,
  };
}

function genSquarePrism() {
  const a = randInt(3, 10);
  const h = randInt(3, 10);

  const baseArea = a * a;
  const perimeter = 4 * a;
  const lateralArea = perimeter * h;
  const totalArea = 2 * baseArea + lateralArea;

  return {
    variant: "square",
    isCube: false,
    a,
    h,
    aDisplay: String(a),
    hDisplay: String(h),
    baseArea,
    perimeter,
    lateralArea,
    totalAreaExpr: String(totalArea),
    totalAreaValue: totalArea,
  };
}

function genCube() {
  const a = randInt(2, 8);
  const totalArea = 6 * a * a;

  return {
    variant: "cube",
    isCube: true,
    a,
    aDisplay: String(a),
    totalAreaExpr: String(totalArea),
    totalAreaValue: totalArea,
  };
}

function genRectanglePrism() {
  const a = randInt(3, 10);
  const b = randInt(3, 10);
  const h = randInt(3, 10);

  const baseArea = a * b;
  const perimeter = 2 * a + 2 * b;
  const lateralArea = perimeter * h;
  const totalArea = 2 * baseArea + lateralArea;

  return {
    variant: "rectangle",
    isCube: false,
    a,
    b,
    h,
    aDisplay: String(a),
    bDisplay: String(b),
    hDisplay: String(h),
    baseArea,
    perimeter,
    lateralArea,
    totalAreaExpr: String(totalArea),
    totalAreaValue: totalArea,
  };
}

function genParallelogramPrism() {
  const a = randInt(3, 10);
  const b = randInt(3, 10); // drugi bok
  let hBase = randInt(3, 8); // wysokość opuszczona na bok a
  if (a * hBase > 150) hBase = 2; // trochę ucywilizuj wartości

  const h = randInt(3, 10);

  const baseArea = a * hBase;
  const perimeter = 2 * a + 2 * b;
  const lateralArea = perimeter * h;
  const totalArea = 2 * baseArea + lateralArea;

  return {
    variant: "parallelogram",
    isCube: false,
    a,
    b,
    baseHeight: hBase,
    h,
    aDisplay: String(a),
    bDisplay: String(b),
    baseHeightDisplay: String(hBase),
    hDisplay: String(h),
    baseArea,
    perimeter,
    lateralArea,
    totalAreaExpr: String(totalArea),
    totalAreaValue: totalArea,
  };
}

function genRhombusPrism() {
  const a = randInt(3, 10);
  let hBase = randInt(3, 8);
  if (a * hBase > 150) hBase = 2;
  const h = randInt(3, 10);

  const baseArea = a * hBase;
  const perimeter = 4 * a;
  const lateralArea = perimeter * h;
  const totalArea = 2 * baseArea + lateralArea;

  return {
    variant: "rhombus",
    isCube: false,
    a,
    baseHeight: hBase,
    h,
    aDisplay: String(a),
    baseHeightDisplay: String(hBase),
    hDisplay: String(h),
    baseArea,
    perimeter,
    lateralArea,
    totalAreaExpr: String(totalArea),
    totalAreaValue: totalArea,
  };
}

function genTrapezoidPrism() {
  const a = randInt(4, 10); // podstawy
  const b = randInt(3, 9);
  const c = randInt(3, 9); // ramiona
  const d = randInt(3, 9);
  let hBase = randInt(3, 8);

  // dopasuj, żeby (a+b)*hBase było parzyste
  if (((a + b) * hBase) % 2 === 1) hBase += 1;

  const h = randInt(3, 10);

  const baseArea = ((a + b) * hBase) / 2;
  const perimeter = a + b + c + d;
  const lateralArea = perimeter * h;
  const totalArea = 2 * baseArea + lateralArea;

  return {
    variant: "trapezoid",
    isCube: false,
    a,
    b,
    c,
    d,
    baseHeight: hBase,
    h,
    aDisplay: String(a),
    bDisplay: String(b),
    cDisplay: String(c),
    dDisplay: String(d),
    baseHeightDisplay: String(hBase),
    hDisplay: String(h),
    baseArea,
    perimeter,
    lateralArea,
    totalAreaExpr: String(totalArea),
    totalAreaValue: totalArea,
  };
}

function genRegularHexagonPrism() {
  // bok parzysty, h = k√3 => Pc = K√3
  const n = randInt(2, 5); // a = 2n
  const a = 2 * n;
  const hCoeff = randInt(1, 5);
  const hValue = hCoeff * Math.sqrt(3);

  const perimeter = 6 * a; // 12n
  const baseCoeff = 6 * n * n; // Pp = 6n^2 √3
  const lateralCoeff = 12 * n * hCoeff; // Pb = 12n*hCoeff √3
  const totalCoeff = 2 * baseCoeff + lateralCoeff; // Pc = totalCoeff √3
  const totalValue = totalCoeff * Math.sqrt(3);

  return {
    variant: "regular_hexagon",
    isCube: false,
    nSides: 6,
    baseName: "sześciokąt foremny",
    a,
    h: hValue,
    aDisplay: String(a),
    hDisplay: `${hCoeff}\\sqrt{3}`,
    perimeter,
    baseCoeff,
    lateralCoeff,
    totalCoeff,
    totalAreaExpr: `${totalCoeff}\\sqrt{3}`,
    totalAreaValue: totalValue,
    hexN: n,
    hRootCoeff: hCoeff,
  };
}

// ====== główny generator ======

function generateTotalSurfaceTaskCore() {
  const variants = [
    "triangle_scalene",
    "triangle_right",
    "triangle_equilateral",
    "square",
    "rectangle",
    "parallelogram",
    "rhombus",
    "trapezoid",
    "regular_hexagon",
    "cube",
  ];

  // prosty los – w razie problemu możemy ponowić
  // (tu nie przewidujemy zwrotu null, więc pętla jednorazowa)
  const v = randomFrom(variants);

  switch (v) {
    case "triangle_scalene":
      return genTriangleScalene();
    case "triangle_right":
      return genTriangleRight();
    case "triangle_equilateral":
      return genTriangleEquilateral();
    case "square":
      return genSquarePrism();
    case "rectangle":
      return genRectanglePrism();
    case "parallelogram":
      return genParallelogramPrism();
    case "rhombus":
      return genRhombusPrism();
    case "trapezoid":
      return genTrapezoidPrism();
    case "regular_hexagon":
      return genRegularHexagonPrism();
    case "cube":
    default:
      return genCube();
  }
}

export function generateTask() {
  const core = generateTotalSurfaceTaskCore();
  return {
    type: "TotalSurfaceTask",
    data: core,
  };
}

// ====== treść zadania ======

export function renderTask(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const {
    variant,
    isCube,
    nSides,
    baseName,
    aDisplay,
    bDisplay,
    cDisplay,
    dDisplay,
    baseHeightDisplay,
    hDisplay,
  } = task;

  if (isCube || variant === "cube") {
    return (
      <p>
        Dany jest sześcian o krawędzi{" "}
        <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} />. Oblicz
        powierzchnię całkowitą tego sześcianu.
      </p>
    );
  }

  // wspólne fragmenty
  if (variant === "triangle_scalene") {
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest trójkąt o bokach{" "}
        <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} />,{" "}
        <InlineMath math={`b = ${bDisplay}\\,\\text{cm}`} /> i{" "}
        <InlineMath math={`c = ${cDisplay}\\,\\text{cm}`} /> oraz wysokości
        opuszczonej na bok{" "}
        <InlineMath math="a" /> równej{" "}
        <InlineMath math={`h_a = ${baseHeightDisplay}\\,\\text{cm}`} />.
        Wysokość graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz powierzchnię
        całkowitą graniastosłupa.
      </p>
    );
  }

  if (variant === "triangle_right") {
  return (
    <p>
      Dany jest graniastosłup prosty, którego podstawą jest trójkąt
      prostokątny o przyprostokątnych{" "}
      <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} /> i{" "}
      <InlineMath math={`b = ${bDisplay}\\,\\text{cm}`} />. Wysokość
      graniastosłupa wynosi{" "}
      <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz
      powierzchnię całkowitą graniastosłupa.
    </p>
  );
}


  if (variant === "triangle_equilateral") {
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest trójkąt
        równoboczny o boku{" "}
        <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} />. Wysokość
        graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz
        powierzchnię całkowitą graniastosłupa.
      </p>
    );
  }

  if (variant === "square") {
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest kwadrat o boku{" "}
        <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} />. Wysokość
        graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz
        powierzchnię całkowitą graniastosłupa.
      </p>
    );
  }

  if (variant === "rectangle") {
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest prostokąt o
        bokach <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} /> i{" "}
        <InlineMath math={`b = ${bDisplay}\\,\\text{cm}`} />. Wysokość
        graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz
        powierzchnię całkowitą graniastosłupa.
      </p>
    );
  }

  if (variant === "parallelogram") {
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest równoległobok o
        bokach <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} /> i{" "}
        <InlineMath math={`b = ${bDisplay}\\,\\text{cm}`} /> oraz wysokości
        opuszczonej na bok{" "}
        <InlineMath math="a" /> równej{" "}
        <InlineMath math={`h_a = ${baseHeightDisplay}\\,\\text{cm}`} />.
        Wysokość graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz
        powierzchnię całkowitą graniastosłupa.
      </p>
    );
  }

  if (variant === "rhombus") {
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest romb o boku{" "}
        <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} /> oraz wysokości
        opuszczonej na ten bok{" "}
        <InlineMath math={`h_a = ${baseHeightDisplay}\\,\\text{cm}`} />.
        Wysokość graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz
        powierzchnię całkowitą graniastosłupa.
      </p>
    );
  }

  if (variant === "trapezoid") {
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest trapez o
        podstawach{" "}
        <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} /> i{" "}
        <InlineMath math={`b = ${bDisplay}\\,\\text{cm}`} /> oraz ramionach{" "}
        <InlineMath math={`c = ${cDisplay}\\,\\text{cm}`} /> i{" "}
        <InlineMath math={`d = ${dDisplay}\\,\\text{cm}`} />. Wysokość trapezu
        wynosi{" "}
        <InlineMath math={`h_a = ${baseHeightDisplay}\\,\\text{cm}`} />, a
        wysokość graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz
        powierzchnię całkowitą graniastosłupa.
      </p>
    );
  }

  if (variant === "regular_hexagon") {
    const name = baseName || `sześciokąt foremny`;
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest {name} o boku{" "}
        <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} />. Wysokość
        graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz
        powierzchnię całkowitą graniastosłupa.
      </p>
    );
  }

  // awaryjnie
  return (
    <p>
      Dany jest graniastosłup prosty. Oblicz jego powierzchnię całkowitą na
      podstawie podanych danych.
    </p>
  );
}

// ====== input ======

export function renderInput(taskOrWrapper, userAnswer, onChange, disabled) {
  unwrapTask(taskOrWrapper);

  return (
    <div className="flex items-center gap-2">
      <InlineMath math="P_c =" />
      <SqrtInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="P_c"
        suffixLatex="\text{cm}^2"
      />
    </div>
  );
}

// ====== Twoja odpowiedź ======

export function renderUserAnswer(userAnswer) {
  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer?.raw ?? userAnswer?.parsed ?? "";

  if (!raw || String(raw).trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  const text = String(raw).trim();
  return <InlineMath math={`P_c = ${text}\\,\\text{cm}^2`} />;
}


// ====== sprawdzanie ======

export function checkAnswer(taskOrWrapper, userAnswer) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return false;

  if (!userAnswer) return false;

  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer.raw ?? userAnswer.parsed ?? "";

  if (!raw || String(raw).trim() === "") return false;

  const val =
    typeof userAnswer === "number"
      ? userAnswer
      : evaluateExpression(raw);

  if (typeof val !== "number" || !isFinite(val)) return false;

  const correct = task.totalAreaValue;
  if (typeof correct !== "number" || !isFinite(correct)) return false;

  return Math.abs(val - correct) < 1e-2;
}

// ====== poprawna odpowiedź ======

export function renderAnswer(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const expr = task.totalAreaExpr || String(task.totalAreaValue);
  return <InlineMath math={`P_c = ${expr}\\,\\text{cm}^2`} />;
}

// ====== wyjaśnienie ======

export function renderExplanation(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const {
    variant,
    isCube,
    a,
    b,
    c,
    d,
    baseHeight,
    h,
    aDisplay,
    bDisplay,
    cDisplay,
    dDisplay,
    baseHeightDisplay,
    hDisplay,
    baseArea,
    perimeter,
    lateralArea,
    baseCoeff,
    lateralCoeff,
    totalCoeff,
  } = task;

  if (isCube || variant === "cube") {
    const a2 = a * a;
    const total = 6 * a2;
    return (
      <div className="space-y-4">
        <p>
          <b>Krok 1:</b> Zapisujemy wzór na powierzchnię całkowitą sześcianu.
        </p>
        <BlockMath math="P_c = 6a^2" />

        <p>
          <b>Krok 2:</b> Podstawiamy dane i obliczamy wartość.
        </p>
        <BlockMath
          math={`P_c = 6\\cdot ${aDisplay}^2 = 6\\cdot ${a2} = ${total}\\,\\text{cm}^2`}
        />
      </div>
    );
  }

  // pomocnicze liczby, jeżeli nie były policzone w generatorze
  let Pp = baseArea;
  let Op = perimeter;
  let Pb = lateralArea;

  if (variant === "triangle_scalene") {
    const PpLocal = (a * baseHeight) / 2;
    const OpLocal = a + b + c;
    const PbLocal = OpLocal * h;

    Pp = PpLocal;
    Op = OpLocal;
    Pb = PbLocal;

    const Pc = 2 * PpLocal + PbLocal;

    return (
      <div className="space-y-4">
        <p>
          <b>Krok 1:</b> Obliczamy pole podstawy – trójkąta dowolnego z boku{" "}
          <InlineMath math="a" /> i wysokości{" "}
          <InlineMath math="h_a" />.
        </p>
        <BlockMath math={tex`P_p = \dfrac{1}{2} a h_a`} />
        <BlockMath
          math={`P_p = \\dfrac{1}{2}\\cdot ${aDisplay}\\cdot ${baseHeightDisplay} = ${PpLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 2:</b> Obliczamy pole powierzchni bocznej graniastosłupa.
        </p>
        <BlockMath math="O_p = a + b + c" />
        <BlockMath
          math={`O_p = ${aDisplay} + ${bDisplay} + ${cDisplay} = ${OpLocal}\\,\\text{cm}`}
        />
        <BlockMath math={tex`P_b = O_p \cdot h`} />
        <BlockMath
          math={`P_b = ${OpLocal}\\cdot ${hDisplay} = ${PbLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 3:</b> Obliczamy powierzchnię całkowitą graniastosłupa.
        </p>
        <BlockMath math="P_c = 2P_p + P_b" />
        <BlockMath
          math={`P_c = 2\\cdot ${PpLocal} + ${PbLocal} = ${Pc}\\,\\text{cm}^2`}
        />
      </div>
    );
  }

    if (variant === "triangle_right") {
      const PpLocal = (a * b) / 2;
      const OpLocal = a + b + c;
      const PbLocal = OpLocal * h;
      const Pc = 2 * PpLocal + PbLocal;

      return (
        <div className="space-y-4">
          <p>
            <b>Krok 1:</b> Korzystając z twierdzenia Pitagorasa obliczamy długość
            przeciwprostokątnej trójkąta w podstawie.
          </p>
          <BlockMath math="c^2 = a^2 + b^2" />
          <BlockMath
            math={`c^2 = ${aDisplay}^2 + ${bDisplay}^2 = ${a * a} + ${
              b * b
            } = ${c * c}`}
          />
          <BlockMath math={`c = ${cDisplay}\\,\\text{cm}`} />

          <p>
            <b>Krok 2:</b> Obliczamy pole podstawy – trójkąta prostokątnego.
          </p>
          <BlockMath math={tex`P_p = \dfrac{1}{2}ab`} />
          <BlockMath
            math={`P_p = \\dfrac{1}{2}\\cdot ${aDisplay}\\cdot ${bDisplay} = ${PpLocal}\\,\\text{cm}^2`}
          />

          <p>
            <b>Krok 3:</b> Obliczamy pole powierzchni bocznej oraz całkowitej
            graniastosłupa.
          </p>
          <BlockMath math="O_p = a + b + c" />
          <BlockMath
            math={`O_p = ${aDisplay} + ${bDisplay} + ${cDisplay} = ${OpLocal}\\,\\text{cm}`}
          />
          <BlockMath math={tex`P_b = O_p \cdot h`} />
          <BlockMath
            math={`P_b = ${OpLocal}\\cdot ${hDisplay} = ${PbLocal}\\,\\text{cm}^2`}
          />
          <BlockMath math="P_c = 2P_p + P_b" />
          <BlockMath
            math={`P_c = 2\\cdot ${PpLocal} + ${PbLocal} = ${Pc}\\,\\text{cm}^2`}
          />
        </div>
      );
    }


  if (variant === "triangle_equilateral") {
    const n = task.equiN; // a = 2n, Pp = n^2√3
    const hCoeff = task.hRootCoeff;
    const PpLocalCoeff = n * n;
    const PpLatex = `${PpLocalCoeff}\\sqrt{3}`;
    const OpLocal = 3 * a;
    const PbLocalCoeff = 6 * n * hCoeff;
    const PbLatex = `${PbLocalCoeff}\\sqrt{3}`;
    const PcCoeff = 2 * PpLocalCoeff + PbLocalCoeff;
    const PcLatex = `${PcCoeff}\\sqrt{3}`;

    return (
      <div className="space-y-4">
        <p>
          <b>Krok 1:</b> Obliczamy pole podstawy – trójkąta równobocznego.
        </p>
        <BlockMath math={tex`P_p = \dfrac{a^2\sqrt{3}}{4}`} />
        <BlockMath
          math={`P_p = \\dfrac{${aDisplay}^2\\sqrt{3}}{4} = ${PpLatex}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 2:</b> Obliczamy pole powierzchni bocznej graniastosłupa.
        </p>
        <BlockMath math="O_p = 3a" />
        <BlockMath
          math={`O_p = 3\\cdot ${aDisplay} = ${OpLocal}\\,\\text{cm}`}
        />
        <BlockMath math={tex`P_b = O_p \cdot h`} />
        <BlockMath
          math={`P_b = ${OpLocal}\\cdot ${hDisplay} = ${PbLatex}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 3:</b> Obliczamy powierzchnię całkowitą graniastosłupa.
        </p>
        <BlockMath math="P_c = 2P_p + P_b" />
        <BlockMath
          math={`P_c = 2\\cdot ${PpLatex} + ${PbLatex} = ${PcLatex}\\,\\text{cm}^2`}
        />
      </div>
    );
  }

  if (variant === "square") {
    const PpLocal = a * a;
    const OpLocal = 4 * a;
    const PbLocal = OpLocal * h;
    const Pc = 2 * PpLocal + PbLocal;

    return (
      <div className="space-y-4">
        <p>
          <b>Krok 1:</b> Obliczamy pole podstawy – kwadratu.
        </p>
        <BlockMath math="P_p = a^2" />
        <BlockMath
          math={`P_p = ${aDisplay}^2 = ${PpLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 2:</b> Obliczamy pole powierzchni bocznej graniastosłupa.
        </p>
        <BlockMath math="O_p = 4a" />
        <BlockMath
          math={`O_p = 4\\cdot ${aDisplay} = ${OpLocal}\\,\\text{cm}`}
        />
        <BlockMath math={tex`P_b = O_p \cdot h`} />
        <BlockMath
          math={`P_b = ${OpLocal}\\cdot ${hDisplay} = ${PbLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 3:</b> Obliczamy powierzchnię całkowitą graniastosłupa.
        </p>
        <BlockMath math="P_c = 2P_p + P_b" />
        <BlockMath
          math={`P_c = 2\\cdot ${PpLocal} + ${PbLocal} = ${Pc}\\,\\text{cm}^2`}
        />
      </div>
    );
  }

  if (variant === "rectangle") {
    const PpLocal = a * b;
    const OpLocal = 2 * a + 2 * b;
    const PbLocal = OpLocal * h;
    const Pc = 2 * PpLocal + PbLocal;

    return (
      <div className="space-y-4">
        <p>
          <b>Krok 1:</b> Obliczamy pole podstawy – prostokąta.
        </p>
        <BlockMath math={tex`P_p = a\cdot b`} />
        <BlockMath
          math={`P_p = ${aDisplay}\\cdot ${bDisplay} = ${PpLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 2:</b> Obliczamy pole powierzchni bocznej graniastosłupa.
        </p>
        <BlockMath math="O_p = 2a + 2b" />
        <BlockMath
          math={`O_p = 2\\cdot ${aDisplay} + 2\\cdot ${bDisplay} = ${OpLocal}\\,\\text{cm}`}
        />
        <BlockMath math={tex`P_b = O_p\cdot h`} />
        <BlockMath
          math={`P_b = ${OpLocal}\\cdot ${hDisplay} = ${PbLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 3:</b> Obliczamy powierzchnię całkowitą graniastosłupa.
        </p>
        <BlockMath math="P_c = 2P_p + P_b" />
        <BlockMath
          math={`P_c = 2\\cdot ${PpLocal} + ${PbLocal} = ${Pc}\\,\\text{cm}^2`}
        />
      </div>
    );
  }

  if (variant === "parallelogram") {
    const PpLocal = a * baseHeight;
    const OpLocal = 2 * a + 2 * b;
    const PbLocal = OpLocal * h;
    const Pc = 2 * PpLocal + PbLocal;

    return (
      <div className="space-y-4">
        <p>
          <b>Krok 1:</b> Obliczamy pole podstawy – równoległoboku.
        </p>
        <BlockMath math={tex`P_p = a\cdot h_a`} />
        <BlockMath
          math={`P_p = ${aDisplay}\\cdot ${baseHeightDisplay} = ${PpLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 2:</b> Obliczamy pole powierzchni bocznej graniastosłupa.
        </p>
        <BlockMath math="O_p = 2a + 2b" />
        <BlockMath
          math={`O_p = 2\\cdot ${aDisplay} + 2\\cdot ${bDisplay} = ${OpLocal}\\,\\text{cm}`}
        />
        <BlockMath math={tex`P_b = O_p\cdot h`} />
        <BlockMath
          math={`P_b = ${OpLocal}\\cdot ${hDisplay} = ${PbLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 3:</b> Obliczamy powierzchnię całkowitą graniastosłupa.
        </p>
        <BlockMath math="P_c = 2P_p + P_b" />
        <BlockMath
          math={`P_c = 2\\cdot ${PpLocal} + ${PbLocal} = ${Pc}\\,\\text{cm}^2`}
        />
      </div>
    );
  }

  if (variant === "rhombus") {
    const PpLocal = a * baseHeight;
    const OpLocal = 4 * a;
    const PbLocal = OpLocal * h;
    const Pc = 2 * PpLocal + PbLocal;

    return (
      <div className="space-y-4">
        <p>
          <b>Krok 1:</b> Obliczamy pole podstawy – rombu.
        </p>
        <BlockMath math={tex`P_p = a\cdot h_a`} />
        <BlockMath
          math={`P_p = ${aDisplay}\\cdot ${baseHeightDisplay} = ${PpLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 2:</b> Obliczamy pole powierzchni bocznej graniastosłupa.
        </p>
        <BlockMath math="O_p = 4a" />
        <BlockMath
          math={`O_p = 4\\cdot ${aDisplay} = ${OpLocal}\\,\\text{cm}`}
        />
        <BlockMath math={tex`P_b = O_p\cdot h`} />
        <BlockMath
          math={`P_b = ${OpLocal}\\cdot ${hDisplay} = ${PbLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 3:</b> Obliczamy powierzchnię całkowitą graniastosłupa.
        </p>
        <BlockMath math="P_c = 2P_p + P_b" />
        <BlockMath
          math={`P_c = 2\\cdot ${PpLocal} + ${PbLocal} = ${Pc}\\,\\text{cm}^2`}
        />
      </div>
    );
  }

  if (variant === "trapezoid") {
    const PpLocal = ((a + b) * baseHeight) / 2;
    const OpLocal = a + b + c + d;
    const PbLocal = OpLocal * h;
    const Pc = 2 * PpLocal + PbLocal;

    return (
      <div className="space-y-4">
        <p>
          <b>Krok 1:</b> Obliczamy pole podstawy – trapezu.
        </p>
        <BlockMath math={tex`P_p = \dfrac{(a+b)h_a}{2}`} />
        <BlockMath
          math={`P_p = \\dfrac{(${aDisplay} + ${bDisplay})\\cdot ${baseHeightDisplay}}{2} = ${PpLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 2:</b> Obliczamy pole powierzchni bocznej graniastosłupa.
        </p>
        <BlockMath math="O_p = a + b + c + d" />
        <BlockMath
          math={`O_p = ${aDisplay} + ${bDisplay} + ${cDisplay} + ${dDisplay} = ${OpLocal}\\,\\text{cm}`}
        />
        <BlockMath math={tex`P_b = O_p\cdot h`} />
        <BlockMath
          math={`P_b = ${OpLocal}\\cdot ${hDisplay} = ${PbLocal}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 3:</b> Obliczamy powierzchnię całkowitą graniastosłupa.
        </p>
        <BlockMath math="P_c = 2P_p + P_b" />
        <BlockMath
          math={`P_c = 2\\cdot ${PpLocal} + ${PbLocal} = ${Pc}\\,\\text{cm}^2`}
        />
      </div>
    );
  }

  if (variant === "regular_hexagon") {
    const n = task.hexN; // a = 2n, Pp = 6n^2√3
    const hCoeff = task.hRootCoeff;
    const PpCoeff = 6 * n * n;
    const PpLatex = `${PpCoeff}\\sqrt{3}`;
    const OpLocal = 6 * a;
    const PbCoeff = 12 * n * hCoeff;
    const PbLatex = `${PbCoeff}\\sqrt{3}`;
    const PcCoeff = 2 * PpCoeff + PbCoeff;
    const PcLatex = `${PcCoeff}\\sqrt{3}`;

    return (
      <div className="space-y-4">
        <p>
          <b>Krok 1:</b> Obliczamy pole podstawy – sześciokąta foremnego jako
          sumę pól sześciu trójkątów równobocznych.
        </p>
        <BlockMath math={tex`P_p = 6\cdot P_{\text{trójkąta}}`} />
        <BlockMath math={tex`P_{\text{trójkąta}} = \dfrac{a^2\sqrt{3}}{4}`} />
        <BlockMath
          math={`P_p = 6\\cdot \\dfrac{${aDisplay}^2\\sqrt{3}}{4} = ${PpLatex}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 2:</b> Obliczamy pole powierzchni bocznej graniastosłupa.
        </p>
        <BlockMath math="O_p = 6a" />
        <BlockMath
          math={`O_p = 6\\cdot ${aDisplay} = ${OpLocal}\\,\\text{cm}`}
        />
        <BlockMath math={tex`P_b = O_p\cdot h`} />
        <BlockMath
          math={`P_b = ${OpLocal}\\cdot ${hDisplay} = ${PbLatex}\\,\\text{cm}^2`}
        />

        <p>
          <b>Krok 3:</b> Obliczamy powierzchnię całkowitą graniastosłupa.
        </p>
        <BlockMath math="P_c = 2P_p + P_b" />
        <BlockMath
          math={`P_c = 2\\cdot ${PpLatex} + ${PbLatex} = ${PcLatex}\\,\\text{cm}^2`}
        />
      </div>
    );
  }

  // awaryjnie
  return (
    <div>
      <p>
        <b>Krok 1:</b> Obliczamy pole podstawy.
      </p>
      <p>
        <b>Krok 2:</b> Obliczamy pole powierzchni bocznej graniastosłupa.
      </p>
      <p>
        <b>Krok 3:</b> Korzystamy ze wzoru{" "}
        <InlineMath math="P_c = 2P_p + P_b" /> i obliczamy powierzchnię
        całkowitą.
      </p>
    </div>
  );
}
